import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tap, map, filter, withLatestFrom } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

import { AppState } from '../app.state';
import { selectIsAuthenticated } from '../user/user.selectors';
import * as UserActions from '../user/user.actions';

import * as LocationActions from './location.actions';
import { selectLocation } from './location.selectors';
import { SavedLocation } from './location.state';

const LOCATION_KEY = 'umkt_location';

function getStorage(isAuthenticated: boolean): Storage | null {
  if (typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined') return null;
  return isAuthenticated ? localStorage : sessionStorage;
}

@Injectable()
export class LocationEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly platformId = inject(PLATFORM_ID);

  persistLocation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActions.setLocation, LocationActions.setRegionId),
        filter(() => isPlatformBrowser(this.platformId)),
        withLatestFrom(
          this.store.select(selectLocation),
          this.store.select(selectIsAuthenticated)
        ),
        tap(([_, location, isAuthenticated]) => {
          try {
            const storage = getStorage(isAuthenticated);
            if (storage && location) {
              storage.setItem(LOCATION_KEY, JSON.stringify(location));
            }
          } catch {
            // ignore storage errors
          }
        })
      ),
    { dispatch: false }
  );

  clearLocationStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActions.clearLocation),
        filter(() => isPlatformBrowser(this.platformId)),
        tap(() => {
          try {
            sessionStorage.removeItem(LOCATION_KEY);
            localStorage.removeItem(LOCATION_KEY);
          } catch {
            // ignore storage errors
          }
        })
      ),
    { dispatch: false }
  );

  loadLocationFromStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadLocationFromStorage),
      filter(() => isPlatformBrowser(this.platformId)),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      map(([_, isAuthenticated]) => {
        try {
          const storage = getStorage(isAuthenticated);
          if (storage) {
            const saved = storage.getItem(LOCATION_KEY);
            if (saved) {
              const location = JSON.parse(saved) as SavedLocation;
              if (location?.city && location?.state) {
                return LocationActions.setLocation({ location });
              }
            }
          }
        } catch {
          // ignore parse errors
        }
        return LocationActions.clearLocation();
      })
    )
  );

  clearLocationOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logoutSuccess),
      map(() => LocationActions.clearLocation())
    )
  );
}
